import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { isFactuFlowSaas } from "../../config/deploymentMode";
import "./UserManualPage.css";

const PRODUCTION_MANUAL_URL = "/docs/";
const LOCAL_MANUAL_URL = "http://localhost:8084/";
const FACTUFLOW_SAAS_MANUAL_URL = "/docs/factuflow-saas-manual.html";

const getDefaultManualUrl = () => {
  const isLocalhost = ["localhost", "127.0.0.1"].includes(window.location.hostname);
  return isLocalhost ? LOCAL_MANUAL_URL : PRODUCTION_MANUAL_URL;
};

const UserManualPage = () => {
  const { t } = useTranslation();
  const manualUrl = useMemo(
    () => isFactuFlowSaas
      ? FACTUFLOW_SAAS_MANUAL_URL
      : process.env.REACT_APP_USER_MANUAL_URL || getDefaultManualUrl(),
    [],
  );
  const [status, setStatus] = useState("checking");

  const checkManualAvailability = useCallback(async () => {
    setStatus("checking");

    try {
      const target = new URL(manualUrl, window.location.origin);
      const isCrossOrigin = target.origin !== window.location.origin;
      const response = await fetch(target.toString(), {
        method: isCrossOrigin ? "HEAD" : "GET",
        mode: isCrossOrigin ? "no-cors" : "same-origin",
        cache: "no-store",
      });

      if (!isCrossOrigin && !response.ok) {
        throw new Error(`Manual unavailable: HTTP ${response.status}`);
      }
      if (!isCrossOrigin) {
        const content = await response.text();
        const expectedMarker = isFactuFlowSaas
          ? 'name="factuflow-saas-manual"'
          : 'name="generator" content="mkdocs-';
        if (!content.includes(expectedMarker)) {
          throw new Error("The configured URL does not contain the IDAX manual");
        }
      }

      setStatus("ready");
    } catch (_error) {
      setStatus("unavailable");
    }
  }, [manualUrl]);

  useEffect(() => {
    checkManualAvailability();
  }, [checkManualAvailability]);

  if (status !== "ready") {
    return (
      <div className="user-manual-page user-manual-status">
        <i
          className={`fa ${status === "checking" ? "fa-spinner fa-spin" : "fa-book-open"}`}
          aria-hidden="true"
        />
        <h2>{t("sidebar.userManual")}</h2>
        <p>
          {t(status === "checking"
            ? "userManual.checking"
            : isFactuFlowSaas ? "userManual.saasUnavailable" : "userManual.unavailable")}
        </p>
        {status === "unavailable" && (
          <button
            type="button"
            className="btn btn-primary"
            onClick={checkManualAvailability}
          >
            <i className="fa fa-refresh" aria-hidden="true" />{" "}
            {t("userManual.retry")}
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="user-manual-page">
      <iframe
        className="user-manual-frame"
        src={manualUrl}
        title={t("sidebar.userManual")}
      />
    </div>
  );
};

export default UserManualPage;
